// src/components/AdminPortal/ContactMessages.js
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { FiEdit, FiTrash2, FiX, FiInfo  } from "react-icons/fi";
import ContentLoader from "../../../global/Content/ContentLoader";
import "./ContactMessages.css";
import AxiosWeb from "../../../utils/axiosWeb";

const ContactMessages = () => {
  const [messages, setMessages] = useState([]);
  const [filteredMessages, setFilteredMessages] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  //const [editingMessage, setEditingMessage] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const messagesPerPage = 10;
  const statusOptions = ["NEW", "ASSIGNED", "COMPLETED"];

 useEffect(() => {
  const fetchMessages = async () => {
    try {
      const res = await AxiosWeb.get(
        `${process.env.REACT_APP_BASE_URL}/contactus/getAllContacts`
      );

      console.log("API Response:", res.data);

      let data = [];

      if (Array.isArray(res.data)) {
        data = res.data;
      } else if (Array.isArray(res.data.data)) {
        data = res.data.data;
      } else if (Array.isArray(res.data.content)) {
        data = res.data.content;
      } else if (Array.isArray(res.data.contacts)) {
        data = res.data.contacts;
      } else {
        console.error("Unexpected response:", res.data);
        setMessages([]);
        return;
      }

      const formatted = data.map((msg, index) => ({
        id: msg.id || `msg${index + 1}`,
        name: msg.name || "",
        email: msg.email || "",
        country: msg.country || "N/A",
        phone: msg.phone || "",
        countryCode: msg.countryCode || "",
        phoneNumber: msg.phoneNumber || "",
        message: msg.message || "",
        subject: msg.subject || "",
        status: msg.status || "NEW",
        createdAt: msg.createdAt || new Date().toISOString(),
      }));

      setMessages(formatted);
    } catch (err) {
      console.error("Fetch Error:", err);
      setError("Failed to load messages.");
    } finally {
      setLoading(false);
    }
  };

  fetchMessages();
}, []);

  // Filter & search
  useEffect(() => {
    const lowerCaseSearchTerm = searchTerm.toLowerCase();
    const searchedMessages = messages.filter(
      (msg) =>
        msg.name.toLowerCase().includes(lowerCaseSearchTerm) ||
        msg.email.toLowerCase().includes(lowerCaseSearchTerm) ||
        msg.country.toLowerCase().includes(lowerCaseSearchTerm) ||
        msg.message.toLowerCase().includes(lowerCaseSearchTerm)
    );

    const sortedMessages = searchedMessages.sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );
    setFilteredMessages(sortedMessages);
    setCurrentPage(1);
  }, [messages, searchTerm]);

  const indexOfLastMessage = currentPage * messagesPerPage;
  const indexOfFirstMessage = indexOfLastMessage - messagesPerPage;
  const currentMessages = filteredMessages.slice(
    indexOfFirstMessage,
    indexOfLastMessage
  );
  const totalPages = Math.ceil(filteredMessages.length / messagesPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleStatusChange = async (id, newStatus) => {
    try {
      setIsProcessing(true);

      await AxiosWeb.put(
        `${process.env.REACT_APP_BASE_URL}/contactus/updateContact?id=${id}`,
        { status: newStatus },
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      setMessages((prev) =>
        prev.map((msg) => (msg.id === id ? { ...msg, status: newStatus } : msg))
      );

      toast.success("Status updated successfully!");
    } catch (error) {
      console.error(error);
      toast.error("Failed to update status");
    } finally {
      setIsProcessing(false);
    }
  };

  // Delete
  const handleDeleteClick = (message) => {
    setMessageToDelete(message);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!messageToDelete) return;

    try {
      setIsProcessing(true);

      await AxiosWeb.delete(
        `${process.env.REACT_APP_BASE_URL}/contactus/deleteContact?id=${messageToDelete.id}`
      );

      setMessages((prev) =>
        prev.filter((msg) => msg.id !== messageToDelete.id)
      );

      toast.success("Message deleted successfully!");
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete message");
    } finally {
      setIsProcessing(false);
      setShowDeleteModal(false);
      setMessageToDelete(null);
    }
  };

  // Edit
  // const handleEditClick = (msg) => setEditingMessage({ ...msg });

  // const handleEditSave = async (e) => {
  //   e.preventDefault();
  //   try {
  //     setIsProcessing(true);

  //     await AxiosWeb.put(
  //       `${process.env.REACT_APP_BASE_URL}/contactus/updateContact?id=${editingMessage.id}`,
  //       editingMessage,
  //       {
  //         headers: { "Content-Type": "application/json" },
  //       }
  //     );

  //     setMessages((prev) =>
  //       prev.map((msg) => (msg.id === editingMessage.id ? editingMessage : msg))
  //     );

  //     toast.success("Message updated successfully!");
  //     setEditingMessage(null);
  //   } catch (error) {
  //     console.error(error);
  //     toast.error("Error updating message");
  //   } finally {
  //     setIsProcessing(false);
  //   }
  // };

  return (
    <div>
      <div className="search-bar" style={{ marginBottom: "10px" }}>
        <input
          type="text"
          placeholder="Search..."
          className="search-input"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="messages-list-section">
        <h3>Contact Us Messages ({filteredMessages.length})</h3>

        {loading ? (
          <ContentLoader />
        ) : error ? (
          <p className="error-message">{error}</p>
        ) : filteredMessages.length === 0 ? (
          <p className="no-messages">No messages found.</p>
        ) : (
          <div className="messages-table-container">
            <table className="messages-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Subject</th>
                  <th>Message</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentMessages.map((msg) => (
                  <tr key={msg.id}>
                    <td>{msg.name}</td>
                    <td>{msg.email}</td>
                    <td>
                      {msg.countryCode
                        ? `${msg.countryCode} ${msg.phoneNumber}`
                        : msg.phone}
                    </td>
                    <td
                      className="message-snippet"
                      style={{ cursor: "pointer", color: "#0d6efd" }}
                      onClick={() => setSelectedMessage(msg)}
                    >
                      {msg.subject?.length > 30
                        ? msg.subject.substring(0, 30) + "..."
                        : msg.subject}
                    </td>
                    <td
                      className="message-snippet"
                      onClick={() => setSelectedMessage(msg)}
                      style={{ cursor: "pointer", color: "#0d6efd" }}
                    >
                      {msg.message.length > 80
                        ? msg.message.substring(0, 80) + "..."
                        : msg.message}
                    </td>
                    <td>
                      <select
                        value={msg.status}
                        onChange={(e) =>
                          handleStatusChange(msg.id, e.target.value)
                        }
                        className={`status-select status-${msg.status.toLowerCase()}`}
                        disabled={isProcessing}
                      >
                        {statusOptions.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>{new Date(msg.createdAt).toLocaleDateString()}</td>
                    <td className="action-column">
                      <button
                        onClick={() => setSelectedMessage(msg)}
                        className="icon-btn info-btn"
                        title="View Details"
                        disabled={isProcessing}
                      >
                        <FiInfo size={18} />
                      </button>

                      <button
                        onClick={() => handleDeleteClick(msg)}
                        className="icon-btn delete-btn"
                        disabled={isProcessing}
                      >
                        <FiTrash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="pagination">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {selectedMessage && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 999,
          }}
          onClick={() => setSelectedMessage(null)}
        >
          <div
            style={{
              background: "#fff",
              width: "450px",
              maxWidth: "95%",
              borderRadius: "10px",
              padding: "25px",
              position: "relative",
              boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedMessage(null)}
              style={{
                position: "absolute",
                top: "20px",
                right: "12px",
                border: "none",
                background: "none",
                cursor: "pointer",
                padding: "5px",
                borderRadius: "50%",
                transition: "0.2s",
              }}
              onMouseEnter={(e) => (e.target.style.background = "#e6e6e6")}
              onMouseLeave={(e) => (e.target.style.background = "none")}
            >
              <FiX size={22} color="#333" />
            </button>

            <h3 style={{ marginBottom: "20px", color: "#2a4e85" }}>
              Message Details
            </h3>

            {/* Subject */}
            <div style={{ marginBottom: "18px" }}>
              <label
                style={{ fontWeight: 600, fontSize: "14px", color: "#2a4e85" }}
              >
                Subject
              </label>
              <div
                style={{
                  background: "#fafafa",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  padding: "12px",
                  marginTop: "5px",
                  fontSize: "14px",
                }}
              >
                {selectedMessage.subject || "No subject"}
              </div>
            </div>

            {/* Message */}
            <div style={{ marginBottom: "18px" }}>
              <label
                style={{ fontWeight: 600, fontSize: "14px", color: "#2a4e85" }}
              >
                Message
              </label>
              <div
                style={{
                  background: "#fafafa",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  padding: "12px",
                  marginTop: "5px",
                  fontSize: "14px",
                  height: "150px",
                  overflowY: "auto",
                  whiteSpace: "pre-wrap",
                }}
              >
                {selectedMessage.message || "No message available"}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {/* {editingMessage && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Edit Contact Message</h3>
              <button
                className="modal-close-btn"
                onClick={() => setEditingMessage(null)}
                disabled={isProcessing}
              >
                <FiX size={22} />
              </button>
            </div>

            <form onSubmit={handleEditSave} className="edit-form">
              <label>
                Name
                <input
                  type="text"
                  value={editingMessage.name}
                  onChange={(e) =>
                    setEditingMessage({
                      ...editingMessage,
                      name: e.target.value,
                    })
                  }
                />
              </label>

              <label>
                Email
                <input
                  type="email"
                  value={editingMessage.email}
                  onChange={(e) =>
                    setEditingMessage({
                      ...editingMessage,
                      email: e.target.value,
                    })
                  }
                />
              </label>

              <label>
                Subject
                <textarea
                  value={editingMessage.subject}
                  onChange={(e) =>
                    setEditingMessage({
                      ...editingMessage,
                      subject: e.target.value,
                    })
                  }
                />
              </label>
              <label>
                Message
                <textarea
                  value={editingMessage.message}
                  onChange={(e) =>
                    setEditingMessage({
                      ...editingMessage,
                      message: e.target.value,
                    })
                  }
                />
              </label>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setEditingMessage(null)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-save"
                  disabled={isProcessing}
                >
                  {isProcessing ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )} */}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div
            className="modal-content"
            style={{ maxWidth: "400px", textAlign: "center" }}
          >
            <h3 style={{ color: "#2a4e85", marginBottom: "15px" }}>
              Confirm Delete
            </h3>
            <p style={{ color: "#555", marginBottom: "25px" }}>
              Are you sure you want to delete this message?
            </p>
            <div className="form-actions" style={{ justifyContent: "center" }}>
              <button
                className="btn-cancel"
                onClick={() => setShowDeleteModal(false)}
                disabled={isProcessing}
              >
                Cancel
              </button>
              <button
                className="btn-save"
                style={{ backgroundColor: "#dc3545" }}
                onClick={confirmDelete}
                disabled={isProcessing}
              >
                {isProcessing ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactMessages;
